import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import confetti from 'canvas-confetti';
import { 
  CheckCircle2, Clock, Calendar, ArrowRight, Loader2, 
  Sun, Moon, Sparkles, TrendingUp, SkipForward, X, Check,
  ChevronRight, Archive, Play, Trash2
} from 'lucide-react';
import DatePickerWithStats from './DatePickerWithStats';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { getPriority } from '../../design-system/priority';

const API = '/api';

const EFFORT_OPTIONS = [
  { value: 1, label: '<15m' },
  { value: 2, label: '15-30m' },
  { value: 3, label: '30-60m' },
  { value: 5, label: '1-3h' },
  { value: 8, label: '3-6h' },
  { value: 13, label: '6h+' },
];

const getTextColorForBg = (bgColor) => {
  if (!bgColor) return '#000000';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#1e293b' : '#ffffff';
};

const EODModal = ({ open, onOpenChange, selectedDate, actingAs, onComplete, onViewReport }) => {
  const { workAreaColors, workAreaName } = useWorkAreas();
  const WORK_AREA_COLORS = workAreaColors;
  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  
  // Track outcomes for analytics
  const [outcomes, setOutcomes] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  
  // Current task action state
  const [selectedEffort, setSelectedEffort] = useState(null);
  const [actionMode, setActionMode] = useState(null); // 'done', 'partial', 'reschedule', 'backlog'
  const [rescheduleDate, setRescheduleDate] = useState(format(new Date(), 'yyyy-MM-dd')); // Default: today
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const tomorrowStr = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const nextWeekStr = format(addDays(new Date(), 7), 'yyyy-MM-dd');
  
  // Partial task state
  const [partialEffort, setPartialEffort] = useState(null);
  const [continuationEffort, setContinuationEffort] = useState(3);

  // Fetch incomplete tasks — skip already-reviewed tasks from existing EOD session
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (actingAs) params.append('acting_as', actingAs);
      
      // Check for existing EOD session for this date
      const summaryRes = await fetch(`${API}/eod-summaries?date=${dateStr}${actingAs ? `&acting_as=${actingAs}` : ''}`, { credentials: 'include' });
      let existingOutcomes = [];
      let existingSummary = null;
      if (summaryRes.ok) {
        const summaries = await summaryRes.json();
        if (summaries.length > 0) {
          existingSummary = summaries[0]; // Most recent for this date
          existingOutcomes = existingSummary.outcomes || [];
        }
      }
      
      const response = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (response.ok) {
        const allTasks = await response.json();
        const reviewedTaskIds = new Set(existingOutcomes.map(o => o.task_id));
        const incompleteTasks = allTasks.filter(t => 
          ['To Do', 'In Progress', 'Blocked'].includes(t.task_status) &&
          !reviewedTaskIds.has(t.task_id)
        );
        setTasks(incompleteTasks);
        setCurrentTaskIndex(0);
        // Carry forward existing outcomes for the continuation
        setOutcomes(existingOutcomes);
        setShowSummary(incompleteTasks.length === 0 && existingOutcomes.length > 0);
      }
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [dateStr, actingAs]);

  useEffect(() => {
    if (open) {
      fetchTasks();
      resetActionState();
    }
  }, [open, fetchTasks]);

  const resetActionState = () => {
    setActionMode(null);
    setSelectedEffort(null);
    setPartialEffort(null);
    setContinuationEffort(3);
    setRescheduleDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  };

  const currentTask = tasks[currentTaskIndex];
  const isPersonal = currentTask?.work_area_id === 'Personal';
  const progress = tasks.length > 0 ? ((currentTaskIndex) / tasks.length) * 100 : 0;

  // Process current task action
  const processAction = async () => {
    if (!currentTask) return;
    setProcessing(true);

    try {
      let updates = {};
      let outcome = {
        task_id: currentTask.task_id,
        title: currentTask.task_title,
        work_area_id: currentTask.work_area_id,
        original_status: currentTask.task_status,
        action: actionMode,
        timestamp: new Date().toISOString(),
        date: dateStr
      };

      if (actionMode === 'done') {
        updates = {
          task_status: 'Done',
          [isPersonal ? 'task_actual_personal_effort' : 'task_actual_effort']: selectedEffort
        };
        outcome.effort_recorded = selectedEffort;
        outcome.new_status = 'Done';
      } else if (actionMode === 'partial') {
        // Mark current task as partial
        updates = {
          task_status: 'Partial',
          [isPersonal ? 'task_actual_personal_effort' : 'task_actual_effort']: partialEffort
        };
        outcome.effort_recorded = partialEffort;
        outcome.new_status = 'Partial';
        outcome.continuation_created = true;

        // Create continuation task with parent link
        const continuationData = {
          task_title: `${currentTask.task_title} (Continuation)`,
          task_description: currentTask.task_description || '',
          work_area_id: currentTask.work_area_id || 'General',
          task_estimated_effort: continuationEffort,
          task_priority: currentTask.task_priority ?? 3,
          task_delivery_date: rescheduleDate,
          task_status: 'To Do',
          task_start_time: null,
          task_end_time: null,
          parent_task_id: currentTask.task_id
        };

        const params = new URLSearchParams();
        if (actingAs) params.append('acting_as', actingAs);
        
        const contResponse = await fetch(`${API}/tasks?${params}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(continuationData)
        });
        
        // Link the original task to its continuation
        if (contResponse.ok) {
          const contTask = await contResponse.json();
          updates.continuation_task_id = contTask.task_id;
        }
      } else if (actionMode === 'reschedule') {
        updates = {
          task_delivery_date: rescheduleDate,
          task_start_time: null,
          task_end_time: null,
          task_reschedule_count: (currentTask.task_reschedule_count || 0) + 1
        };
        outcome.new_date = rescheduleDate;
        outcome.new_status = currentTask.task_status;
      } else if (actionMode === 'backlog') {
        updates = {
          task_delivery_date: null,
          task_start_time: null,
          task_end_time: null
        };
        outcome.moved_to_backlog = true;
        outcome.new_status = currentTask.task_status;
      }

      // Update the task
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);

      const response = await fetch(`${API}/tasks/${currentTask.task_id}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setOutcomes(prev => [...prev, outcome]);
        
        // Move to next task or show summary
        if (currentTaskIndex < tasks.length - 1) {
          setCurrentTaskIndex(prev => prev + 1);
          resetActionState();
        } else {
          // Save EOD summary to database
          await saveEODSummary([...outcomes, outcome]);
          setShowSummary(true);
          
          // Full-screen multicolor confetti celebration
          const confettiCanvas = document.createElement('canvas');
          confettiCanvas.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99999;pointer-events:none;';
          document.body.appendChild(confettiCanvas);
          const myConfetti = confetti.create(confettiCanvas, { resize: true, useWorker: true });
          
          const colors = ['#ff0000','#ff6600','#ffcc00','#33cc33','#0099ff','#6633ff','#ff33cc','#00ffcc','#ff3366','#66ff33','#3366ff','#ff9900'];
          const duration = 5000;
          const end = Date.now() + duration;
          
          // Play triumphant success sound
          try {
            const audio = new Audio('/eod-triumph.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch(e) { /* audio not available */ }
          
          // Intense confetti bursts from multiple origins
          const fireConfetti = () => {
            const timeLeft = end - Date.now();
            if (timeLeft <= 0) {
              setTimeout(() => confettiCanvas.remove(), 2000);
              return;
            }
            const ticks = Math.max(20, 60 * (timeLeft / duration));
            // Left cannon
            myConfetti({ particleCount: ticks, angle: 60, spread: 80, startVelocity: 55, origin: { x: 0, y: 0.7 }, colors, ticks: 200, gravity: 0.8, scalar: 1.2 });
            // Right cannon
            myConfetti({ particleCount: ticks, angle: 120, spread: 80, startVelocity: 55, origin: { x: 1, y: 0.7 }, colors, ticks: 200, gravity: 0.8, scalar: 1.2 });
            // Center burst
            myConfetti({ particleCount: Math.floor(ticks / 2), spread: 160, startVelocity: 40, origin: { x: 0.5, y: 0.3 }, colors, ticks: 200, gravity: 1, scalar: 1 });
            requestAnimationFrame(fireConfetti);
          };
          fireConfetti();
        }
      } else {
        throw new Error('Failed to update task');
      }
    } catch (error) {
      toast.error('Failed to process task');
    } finally {
      setProcessing(false);
    }
  };

  // Save EOD summary for analytics
  const saveEODSummary = async (allOutcomes) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);

      await fetch(`${API}/eod-summary?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: dateStr,
          outcomes: allOutcomes,
          completed_at: new Date().toISOString(),
          total_tasks: allOutcomes.length,
          done_count: allOutcomes.filter(o => o.action === 'done').length,
          partial_count: allOutcomes.filter(o => o.action === 'partial').length,
          rescheduled_count: allOutcomes.filter(o => o.action === 'reschedule').length,
          backlog_count: allOutcomes.filter(o => o.action === 'backlog').length,
          deleted_count: allOutcomes.filter(o => o.action === 'deleted').length,
          total_effort: allOutcomes.reduce((sum, o) => sum + (o.effort_recorded || 0), 0)
        })
      });
    } catch (error) {
      console.error('Failed to save EOD summary:', error);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Always refresh dashboard after EOD interaction (summary may have been saved)
    if (onComplete) onComplete();
  };

  // Skip current task (move to backlog without processing)
  const skipTask = async () => {
    setActionMode('backlog');
    setTimeout(() => processAction(), 100);
  };

  const accentColor = isPersonal ? 'sky' : 'emerald';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 [&>button]:hidden" data-testid="eod-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Moon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">End of Day Review</h2>
                <p className="text-white/80 text-sm">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Progress bar */}
          {!showSummary && tasks.length > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-white/80 mb-1">
                <span>Progress</span>
                <span>{currentTaskIndex} of {tasks.length} reviewed</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full orka-surface-raised orka-transition-colors duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin orka-text-intent" />
            </div>
          ) : showSummary ? (
            // Summary View
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 mb-4">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold orka-text-primary">Day Complete!</h3>
                <p className="orka-text-tertiary mt-1">Here's your end of day summary</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="orka-bg-success-soft rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold orka-text-success">
                    {outcomes.filter(o => o.action === 'done').length}
                  </div>
                  <div className="text-xs orka-text-success font-medium">Completed</div>
                </div>
                <div className="orka-bg-info-soft rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold orka-text-info">
                    {outcomes.filter(o => o.action === 'partial').length}
                  </div>
                  <div className="text-xs orka-text-info font-medium">Partial</div>
                </div>
                <div className="orka-bg-warning-soft rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold orka-text-warning">
                    {outcomes.filter(o => o.action === 'reschedule').length}
                  </div>
                  <div className="text-xs orka-text-warning font-medium">Rescheduled</div>
                </div>
                <div className="bg-slate-100 slate-700 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold orka-text-secondary">
                    {outcomes.filter(o => o.action === 'backlog').length}
                  </div>
                  <div className="text-xs orka-text-secondary font-medium">To Backlog</div>
                </div>
              </div>

              {/* Outcomes Table */}
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="orka-surface-subtle">
                    <tr>
                      <th className="text-left p-3 font-semibold orka-text-secondary">Task</th>
                      <th className="text-left p-3 font-semibold orka-text-secondary">Work Area</th>
                      <th className="text-left p-3 font-semibold orka-text-secondary">Action</th>
                      <th className="text-left p-3 font-semibold orka-text-secondary">Effort</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {outcomes.map((outcome, idx) => (
                      <tr key={idx} className="hover:orka-surface-subtle dark:hover:bg-slate-800">
                        <td className="p-3">
                          <span className="font-medium orka-text-primary line-clamp-1">{outcome.title}</span>
                        </td>
                        <td className="p-3">
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-medium"
                            style={{ 
                              backgroundColor: WORK_AREA_COLORS[outcome.work_area_id] || '#E5E7EB',
                              color: getTextColorForBg(WORK_AREA_COLORS[outcome.work_area_id] || '#E5E7EB')
                            }}
                          >
                            {workAreaName(outcome.work_area_id) || 'General'}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge className={
                            outcome.action === 'done' ? 'bg-emerald-100 text-emerald-700' :
                            outcome.action === 'partial' ? 'bg-cyan-100 text-cyan-700' :
                            outcome.action === 'reschedule' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 dark:bg-slate-700 orka-text-secondary'
                          }>
                            {outcome.action === 'done' ? 'Completed' :
                             outcome.action === 'partial' ? 'Partial' :
                             outcome.action === 'reschedule' ? `→ ${outcome.new_date}` :
                             'To Backlog'}
                          </Badge>
                        </td>
                        <td className="p-3 orka-text-secondary">
                          {outcome.effort_recorded ? `${outcome.effort_recorded} pts` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Total Effort */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 orka-text-intent" />
                  <span className="font-medium orka-text-secondary">Total Effort Logged</span>
                </div>
                <span className="text-xl font-bold orka-text-intent">
                  {outcomes.reduce((sum, o) => sum + (o.effort_recorded || 0), 0)} points
                </span>
              </div>
            </div>
          ) : tasks.length === 0 ? (
            // No tasks - All Clear with optional report link
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full orka-bg-success-soft mb-4">
                <CheckCircle2 className="h-8 w-8 orka-text-success" />
              </div>
              <h3 className="text-xl font-bold orka-text-primary">All Clear!</h3>
              <p className="orka-text-tertiary mt-1">No incomplete tasks for this day</p>
              {onViewReport && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => { onOpenChange(false); onViewReport(); }}
                  data-testid="eod-allclear-report-link"
                >
                  <TrendingUp className="w-4 h-4 mr-1.5" />View EOD Report
                </Button>
              )}
            </div>
          ) : currentTask ? (
            // Task Review
            <div className="space-y-6">
              {/* Current Task Card */}
              <div className="orka-surface-subtle rounded-xl p-4 border border orka-border-subtle">
                <div className="flex items-start gap-3">
                  <div 
                    className="px-2 py-1 rounded text-xs font-bold shrink-0"
                    style={{ 
                      backgroundColor: getPriority(currentTask.task_priority).color,
                      color: 'var(--orka-text-inverse)'
                    }}
                  >
                    P{currentTask.task_priority}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold orka-text-primary text-lg">{currentTask.task_title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: WORK_AREA_COLORS[currentTask.work_area_id] || '#E5E7EB',
                          color: getTextColorForBg(WORK_AREA_COLORS[currentTask.work_area_id] || '#E5E7EB')
                        }}
                      >
                        {workAreaName(currentTask.work_area_id) || 'General'}
                      </span>
                      <span className="text-xs orka-text-tertiary">•</span>
                      <span className="text-xs orka-text-tertiary">Est. {currentTask.task_estimated_effort} pts</span>
                      {currentTask.task_status !== 'To Do' && (
                        <>
                          <span className="text-xs orka-text-tertiary">•</span>
                          <Badge className="text-xs orka-bg-info-soft orka-text-info">{currentTask.task_status}</Badge>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Selection */}
              {!actionMode ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium orka-text-secondary text-center">What happened with this task?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setActionMode('done')}
                      className={`p-4 rounded-xl border-2 orka-transition-colors hover:shadow-md ${
                        isPersonal 
                          ? 'border-sky-200 bg-sky-50 hover:border-sky-400' 
                          : 'border-emerald-200 bg-emerald-50 hover:border-emerald-400'
                      }`}
                      data-testid="eod-done-btn"
                    >
                      <CheckCircle2 className={`h-8 w-8 mx-auto mb-2 ${isPersonal ? 'text-sky-600' : 'text-emerald-600'}`} />
                      <div className={`font-semibold ${isPersonal ? 'text-sky-700' : 'text-emerald-700'}`}>
                        {isPersonal ? 'Personal Task Done' : 'Completed'}
                      </div>
                      <div className="text-xs orka-text-tertiary mt-1">Mark as fully complete</div>
                    </button>

                    <button
                      onClick={() => setActionMode('partial')}
                      className={`p-4 rounded-xl border-2 orka-transition-colors hover:shadow-md ${
                        isPersonal 
                          ? 'border-sky-200 bg-sky-50 hover:border-sky-400' 
                          : 'border-cyan-200 bg-cyan-50 hover:border-cyan-400'
                      }`}
                      data-testid="eod-partial-btn"
                    >
                      <Play className={`h-8 w-8 mx-auto mb-2 ${isPersonal ? 'text-sky-600' : 'text-cyan-600'}`} />
                      <div className={`font-semibold ${isPersonal ? 'text-sky-700' : 'text-cyan-700'}`}>
                        {isPersonal ? 'Personal Partial' : 'Partial Progress'}
                      </div>
                      <div className="text-xs orka-text-tertiary mt-1">Made some progress</div>
                    </button>

                    <button
                      onClick={() => setActionMode('reschedule')}
                      className="p-4 rounded-xl border-2 orka-border-subtle orka-bg-warning-soft hover:border-amber-400 orka-transition-colors hover:shadow-md"
                      data-testid="eod-reschedule-btn"
                    >
                      <Calendar className="h-8 w-8 mx-auto mb-2 orka-text-warning" />
                      <div className="font-semibold orka-text-warning">Reschedule</div>
                      <div className="text-xs orka-text-tertiary mt-1">Move to another day</div>
                    </button>

                    <button
                      onClick={() => setActionMode('backlog')}
                      className="p-4 rounded-xl border-2 border orka-border-subtle orka-surface-subtle hover:border-slate-400 orka-transition-colors hover:shadow-md"
                      data-testid="eod-backlog-btn"
                    >
                      <Archive className="h-8 w-8 mx-auto mb-2 orka-text-secondary" />
                      <div className="font-semibold orka-text-secondary">To Backlog</div>
                      <div className="text-xs orka-text-tertiary mt-1">Remove date</div>
                    </button>
                  </div>
                  
                  {/* Delete task option */}
                  <div className="text-center pt-2 border-t orka-border-subtle">
                    <button
                      onClick={() => setActionMode('delete')}
                      className="text-xs text-red-400 hover:orka-text-danger transition-colors"
                      data-testid="eod-delete-btn"
                    >
                      Delete task permanently
                    </button>
                  </div>
                </div>
              ) : actionMode === 'done' ? (
                // Done workflow
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <button onClick={resetActionState} className="text-sm orka-text-tertiary hover:orka-text-secondary dark:hover:text-slate-300 flex items-center gap-1">
                      ← Back
                    </button>
                    <span className={`text-sm font-medium ${isPersonal ? 'text-sky-600' : 'text-emerald-600'}`}>
                      {isPersonal ? 'Personal Effort Tracking' : 'Log Effort'}
                    </span>
                  </div>
                  
                  <p className="text-sm orka-text-secondary text-center">
                    {isPersonal ? 'How much personal effort did you spend?' : 'How much effort did this take?'}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    {EFFORT_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSelectedEffort(opt.value)}
                        className={`flex flex-col items-center px-4 py-3 rounded-lg orka-transition-colors ${
                          selectedEffort === opt.value 
                            ? isPersonal 
                              ? 'bg-sky-500 text-white ring-2 ring-sky-300' 
                              : 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                            : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 orka-text-secondary'
                        }`}
                        data-testid={`eod-effort-${opt.value}`}
                      >
                        <span className="text-xl font-bold">{opt.value}</span>
                        <span className="text-xs opacity-80">{opt.label}</span>
                      </button>
                    ))}
                  </div>

                  {isPersonal && (
                    <p className="text-xs text-center orka-text-info font-medium orka-bg-info-soft rounded-lg py-2 px-3">
                      Personal effort tracked separately from work tasks
                    </p>
                  )}
                </div>
              ) : actionMode === 'partial' ? (
                // Partial workflow
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <button onClick={resetActionState} className="text-sm orka-text-tertiary hover:orka-text-secondary dark:hover:text-slate-300 flex items-center gap-1">
                      ← Back
                    </button>
                    <span className={`text-sm font-medium ${isPersonal ? 'text-sky-600' : 'text-cyan-600'}`}>
                      {isPersonal ? 'Personal Partial Progress' : 'Partial Progress'}
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm orka-text-secondary mb-2">
                        {isPersonal ? 'Personal effort completed so far:' : 'Effort completed so far:'}
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {EFFORT_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setPartialEffort(opt.value)}
                            className={`flex flex-col items-center px-3 py-2 rounded-lg orka-transition-colors ${
                              partialEffort === opt.value 
                                ? isPersonal 
                                  ? 'bg-sky-500 text-white ring-2 ring-sky-300' 
                                  : 'bg-cyan-500 text-white ring-2 ring-cyan-300'
                                : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 orka-text-secondary'
                            }`}
                          >
                            <span className="text-lg font-bold">{opt.value}</span>
                            <span className="text-xs opacity-80">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm orka-text-secondary mb-2">Schedule continuation task:</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium orka-text-tertiary mb-1 block">Date</label>
                          <DatePickerWithStats
                            value={rescheduleDate}
                            onChange={(val) => setRescheduleDate(val)}
                            triggerClassName="h-9 w-full"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium orka-text-tertiary mb-1 block">Remaining Effort</label>
                          <select 
                            value={continuationEffort}
                            onChange={(e) => setContinuationEffort(parseInt(e.target.value))}
                            className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                          >
                            {EFFORT_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.value} - {opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {isPersonal && (
                      <p className="text-xs text-center orka-text-info font-medium orka-bg-info-soft rounded-lg py-2 px-3">
                        Personal effort tracked separately from work tasks
                      </p>
                    )}
                  </div>
                </div>
              ) : actionMode === 'reschedule' ? (
                // Reschedule workflow
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <button onClick={resetActionState} className="text-sm orka-text-tertiary hover:orka-text-secondary dark:hover:text-slate-300 flex items-center gap-1">
                      ← Back
                    </button>
                    <span className="text-sm font-medium orka-text-warning">Reschedule Task</span>
                  </div>
                  
                  <div className="space-y-3">
                    <p className="text-sm orka-text-secondary text-center">Move this task to:</p>
                    <DatePickerWithStats
                      value={rescheduleDate}
                      onChange={(val) => setRescheduleDate(val)}
                      triggerClassName="h-10 w-full"
                    />
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant={rescheduleDate === todayStr ? "default" : "outline"}
                        size="sm"
                        onClick={() => setRescheduleDate(todayStr)}
                      >
                        Today
                      </Button>
                      <Button
                        variant={rescheduleDate === tomorrowStr ? "default" : "outline"}
                        size="sm"
                        onClick={() => setRescheduleDate(tomorrowStr)}
                      >
                        Tomorrow
                      </Button>
                      <Button
                        variant={rescheduleDate === nextWeekStr ? "default" : "outline"}
                        size="sm"
                        onClick={() => setRescheduleDate(nextWeekStr)}
                      >
                        Next Week
                      </Button>
                    </div>
                  </div>
                </div>
              ) : actionMode === 'backlog' ? (
                // Backlog confirmation
                <div className="space-y-4 text-center">
                  <div className="flex items-center justify-between">
                    <button onClick={resetActionState} className="text-sm orka-text-tertiary hover:orka-text-secondary dark:hover:text-slate-300 flex items-center gap-1">
                      ← Back
                    </button>
                    <span className="text-sm font-medium orka-text-secondary">Move to Backlog</span>
                  </div>
                  
                  <div className="py-4">
                    <Archive className="h-12 w-12 mx-auto orka-text-tertiary mb-3" />
                    <p className="orka-text-secondary">
                      This task will be moved to the backlog (no scheduled date).
                    </p>
                  </div>
                </div>
              ) : actionMode === 'delete' ? (
                // Delete confirmation
                <div className="space-y-4 text-center">
                  <div className="flex items-center justify-between">
                    <button onClick={resetActionState} className="text-sm orka-text-tertiary hover:orka-text-secondary dark:hover:text-slate-300 flex items-center gap-1">
                      ← Back
                    </button>
                    <span className="text-sm font-medium orka-text-danger">Delete Task</span>
                  </div>
                  <div className="py-4">
                    <Trash2 className="h-12 w-12 mx-auto text-red-400 mb-3" />
                    <p className="orka-text-secondary">
                      This will permanently delete this task.
                    </p>
                    {currentTask?.is_calendar_import && (
                      <p className="text-xs orka-text-warning mt-2">
                        This is a calendar task — it will also be removed from Google Calendar.
                      </p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {!loading && !showSummary && tasks.length > 0 && currentTask && (
          <div className="border-t p-4 orka-surface-subtle flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={skipTask}
              disabled={processing}
              className="orka-text-tertiary"
            >
              <SkipForward className="h-4 w-4 mr-1" />
              Skip to Backlog
            </Button>

            {actionMode && (
              <Button
                onClick={actionMode === 'delete' ? async () => {
                  setProcessing(true);
                  let deleted = false;
                  try {
                    const params = new URLSearchParams();
                    if (actingAs) params.append('acting_as', actingAs);
                    const url = `${API}/tasks/${currentTask.task_id}${params.toString() ? '?' + params.toString() : ''}`;
                    const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
                    // Accept any 2xx as success
                    deleted = res.status >= 200 && res.status < 300;
                    if (!deleted) {
                      const err = await res.json().catch(() => ({}));
                      toast.error(err.detail || 'Failed to delete task');
                    }
                  } catch (e) {
                    // Network errors — but task may already be deleted server-side
                    console.error('Delete request error:', e);
                    toast.error('Network error — check if task was deleted');
                  }
                  setProcessing(false);
                  // Move to next task OUTSIDE the try/catch so it never masks the delete result
                  if (deleted) {
                    const newOutcomes = [...outcomes, { task_id: currentTask.task_id, title: currentTask.task_title, action: 'deleted' }];
                    setOutcomes(newOutcomes);
                    toast.success('Task deleted');
                    // Advance to next task or show summary
                    if (currentTaskIndex < tasks.length - 1) {
                      setCurrentTaskIndex(prev => prev + 1);
                    } else {
                      await saveEODSummary(newOutcomes);
                      setShowSummary(true);
                    }
                    setActionMode(null);
                  }
                } : processAction}
                disabled={
                  processing || 
                  (actionMode === 'done' && selectedEffort === null) ||
                  (actionMode === 'partial' && partialEffort === null)
                }
                className={
                  actionMode === 'done' 
                    ? isPersonal ? 'bg-sky-600 hover:bg-sky-700' : 'bg-emerald-600 hover:bg-emerald-700'
                    : actionMode === 'partial'
                    ? isPersonal ? 'bg-sky-600 hover:bg-sky-700' : 'bg-cyan-600 hover:bg-cyan-700'
                    : actionMode === 'reschedule'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : actionMode === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-slate-600 hover:bg-slate-700'
                }
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : actionMode === 'delete' ? (
                  <Trash2 className="h-4 w-4 mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {actionMode === 'done' ? 'Mark Complete' :
                 actionMode === 'partial' ? 'Save & Continue' :
                 actionMode === 'reschedule' ? 'Reschedule' :
                 actionMode === 'delete' ? 'Delete Permanently' :
                 'Move to Backlog'}
              </Button>
            )}
          </div>
        )}

        {/* Summary Footer */}
        {showSummary && (
          <div className="border-t p-4 orka-surface-subtle flex justify-end">
            <Button onClick={handleClose} className="orka-bg-intent hover:bg-indigo-700">
              <Check className="h-4 w-4 mr-2" />
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EODModal;
